/**
 * React Query hooks for Schedule & Shift Management API
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';
import * as scheduleApi from '../lib/api/schedule.client';
// ==================== Query Keys ====================
export const scheduleKeys = {
    all: ['schedules'],
    lists: () => [...scheduleKeys.all, 'list'],
    list: (filters) => [...scheduleKeys.lists(), filters],
    details: () => [...scheduleKeys.all, 'detail'],
    detail: (id) => [...scheduleKeys.details(), id],
};
export const shiftKeys = {
    all: ['shifts'],
    lists: () => [...shiftKeys.all, 'list'],
    list: (scheduleId, startDate, endDate) => [...shiftKeys.lists(), scheduleId, startDate, endDate],
    mySchedule: (filters) => [...shiftKeys.all, 'my-schedule', filters],
    teamSchedule: (filters) => [...shiftKeys.all, 'team-schedule', filters],
};
export const templateKeys = {
    all: ['shift-templates'],
    lists: () => [...templateKeys.all, 'list'],
    list: (scheduleId) => [...templateKeys.lists(), scheduleId],
};
export const exceptionKeys = {
    all: ['shift-exceptions'],
    lists: () => [...exceptionKeys.all, 'list'],
    list: (scheduleId, filters) => [...exceptionKeys.lists(), scheduleId, filters],
};
export const assignmentKeys = {
    all: ['schedule-assignments'],
    lists: () => [...assignmentKeys.all, 'list'],
    list: (scheduleId, filters) => [...assignmentKeys.lists(), scheduleId, filters],
    mySchedules: (filters) => [...assignmentKeys.all, 'my-schedules', filters],
};
export const swapKeys = {
    all: ['shift-swaps'],
    lists: () => [...swapKeys.all, 'list'],
    list: (filters) => [...swapKeys.lists(), filters],
};
// ==================== Schedule Queries ====================
/**
 * List all schedules
 */
export function useListSchedules(params) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: scheduleKeys.list(params || {}),
        queryFn: () => scheduleApi.listSchedules(token, tenantId, params),
        enabled: !!token && !!tenantId,
    });
}
/**
 * Get a specific schedule
 */
export function useGetSchedule(scheduleId) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: scheduleKeys.detail(scheduleId),
        queryFn: () => scheduleApi.getSchedule(token, tenantId, scheduleId),
        enabled: !!token && !!tenantId && !!scheduleId,
    });
}
/**
 * List shifts for a schedule
 */
export function useListShifts(scheduleId, startDate, endDate) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: shiftKeys.list(scheduleId, startDate, endDate),
        queryFn: () => scheduleApi.listShifts(token, tenantId, scheduleId, startDate, endDate),
        enabled: !!token && !!tenantId && !!scheduleId,
    });
}
/**
 * Create a new schedule
 */
export function useCreateSchedule() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (data) => scheduleApi.createSchedule(token, tenantId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
        },
    });
}
/**
 * Update a schedule
 */
export function useUpdateSchedule(scheduleId) {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (data) => scheduleApi.updateSchedule(token, tenantId, scheduleId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(scheduleId) });
        },
    });
}
/**
 * Publish a schedule
 */
export function usePublishSchedule() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (scheduleId) => scheduleApi.publishSchedule(token, tenantId, scheduleId),
        onSuccess: (_, scheduleId) => {
            queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(scheduleId) });
        },
    });
}
// ==================== Shift Mutations ====================
/**
 * Create a new shift
 */
export function useCreateShift() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (data) => scheduleApi.createShift(token, tenantId, data),
        onSuccess: (shift) => {
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(shift.scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.list(shift.scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.teamSchedule({}) });
        },
    });
}
/**
 * Update a shift
 */
export function useUpdateShift(shiftId) {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (data) => scheduleApi.updateShift(token, tenantId, shiftId, data),
        onSuccess: (shift) => {
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(shift.scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.teamSchedule({}) });
        },
    });
}
/**
 * Delete a shift
 */
export function useDeleteShift() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: ({ shiftId }) => scheduleApi.deleteShift(token, tenantId, shiftId),
        onSuccess: (_, { scheduleId }) => {
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.list(scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.teamSchedule({}) });
        },
    });
}
/**
 * Assign an employee to a shift
 */
export function useAssignShift() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: ({ shiftId, employeeId }) => scheduleApi.assignShift(token, tenantId, shiftId, employeeId),
        onSuccess: (shift) => {
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(shift.scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.teamSchedule({}) });
        },
    });
}
// ==================== Employee Views ====================
/**
 * Get my assigned shifts (employee view)
 */
export function useMySchedule(params) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: shiftKeys.mySchedule(params || {}),
        queryFn: () => scheduleApi.getMySchedule(token, tenantId, params),
        enabled: !!token && !!tenantId,
    });
}
/**
 * Get team schedule (manager view)
 */
export function useTeamSchedule(params) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: shiftKeys.teamSchedule(params || {}),
        queryFn: () => scheduleApi.getTeamSchedule(token, tenantId, params),
        enabled: !!token && !!tenantId,
    });
}
// ==================== Shift Templates ====================
/**
 * List all shift templates for a schedule
 */
export function useListTemplates(scheduleId) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: templateKeys.list(scheduleId),
        queryFn: () => scheduleApi.listTemplates(token, tenantId, scheduleId),
        enabled: !!token && !!tenantId && !!scheduleId,
    });
}
/**
 * Create a shift template
 */
export function useCreateTemplate() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: ({ scheduleId, data }) => scheduleApi.createTemplate(token, tenantId, scheduleId, data),
        onSuccess: (_, { scheduleId }) => {
            queryClient.invalidateQueries({ queryKey: templateKeys.list(scheduleId) });
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(scheduleId) });
        },
    });
}
/**
 * Batch create shift templates (up to 25 at once)
 */
export function useBatchCreateTemplates() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: ({ scheduleId, templates }) => scheduleApi.batchCreateTemplates(token, tenantId, scheduleId, templates),
        onSuccess: (_, { scheduleId }) => {
            queryClient.invalidateQueries({ queryKey: templateKeys.list(scheduleId) });
            queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(scheduleId) });
        },
    });
}
// ==================== Shift Exceptions ====================
/**
 * List exceptions for a schedule
 */
export function useListExceptions(scheduleId, params) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: exceptionKeys.list(scheduleId, params || {}),
        queryFn: () => scheduleApi.listExceptions(token, tenantId, scheduleId, params),
        enabled: !!token && !!tenantId && !!scheduleId,
    });
}
/**
 * Create a shift exception (cancel, modify, or add a shift for a specific date)
 */
export function useCreateException(scheduleId) {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (data) => scheduleApi.createException(token, tenantId, scheduleId, data),
        onSuccess: () => {
            // Invalidate exceptions list
            queryClient.invalidateQueries({ queryKey: exceptionKeys.list(scheduleId, {}) });
            // Invalidate computed shifts (since they depend on exceptions)
            queryClient.invalidateQueries({ queryKey: shiftKeys.list(scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.teamSchedule({}) });
        },
    });
}
/**
 * Delete a shift exception
 */
export function useDeleteException(scheduleId) {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (exceptionId) => scheduleApi.deleteException(token, tenantId, scheduleId, exceptionId),
        onSuccess: () => {
            // Invalidate exceptions list
            queryClient.invalidateQueries({ queryKey: exceptionKeys.list(scheduleId, {}) });
            // Invalidate computed shifts (since they depend on exceptions)
            queryClient.invalidateQueries({ queryKey: shiftKeys.list(scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.teamSchedule({}) });
        },
    });
}
// ==================== Shift Swaps ====================
/**
 * List shift swaps
 */
export function useListShiftSwaps(params) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: swapKeys.list(params || {}),
        queryFn: () => scheduleApi.listShiftSwaps(token, tenantId, params),
        enabled: !!token && !!tenantId,
    });
}
/**
 * Request a shift swap
 */
export function useRequestShiftSwap() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (data) => scheduleApi.requestShiftSwap(token, tenantId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: swapKeys.lists() });
        },
    });
}
/**
 * Review a shift swap (manager/admin only)
 */
export function useReviewShiftSwap() {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: ({ swapId, data }) => scheduleApi.reviewShiftSwap(token, tenantId, swapId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: swapKeys.lists() });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.teamSchedule({}) });
        },
    });
}
// ==================== Schedule Assignments ====================
/**
 * List assignments for a schedule (manager view)
 */
export function useListAssignments(scheduleId, params) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: assignmentKeys.list(scheduleId, params || {}),
        queryFn: () => scheduleApi.listAssignments(token, tenantId, scheduleId, params?.status),
        enabled: !!token && !!tenantId && !!scheduleId,
    });
}
/**
 * Get my schedules (employee view)
 */
export function useGetMySchedules(params) {
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useQuery({
        queryKey: assignmentKeys.mySchedules(params || {}),
        queryFn: () => scheduleApi.getMySchedules(token, tenantId, params?.status),
        enabled: !!token && !!tenantId,
    });
}
/**
 * Create assignment - Assign schedule to employee (manager only)
 */
export function useCreateAssignment(scheduleId) {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (data) => scheduleApi.createAssignment(token, tenantId, scheduleId, data),
        onSuccess: () => {
            // Invalidate assignments list for this schedule
            queryClient.invalidateQueries({ queryKey: assignmentKeys.list(scheduleId, {}) });
            // Invalidate employee's my-schedules (if they're viewing their own)
            queryClient.invalidateQueries({ queryKey: assignmentKeys.mySchedules({}) });
            // Invalidate computed shifts (assignments affect what shifts are visible)
            queryClient.invalidateQueries({ queryKey: shiftKeys.list(scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
        },
    });
}
/**
 * Delete assignment - Unassign employee from schedule (manager only)
 */
export function useDeleteAssignment(scheduleId) {
    const queryClient = useQueryClient();
    const token = useAuthStore((state) => state.tokens?.idToken);
    const tenantId = useAuthStore((state) => state.user?.tenantId);
    return useMutation({
        mutationFn: (employeeId) => scheduleApi.deleteAssignment(token, tenantId, scheduleId, employeeId),
        onSuccess: () => {
            // Invalidate assignments list for this schedule
            queryClient.invalidateQueries({ queryKey: assignmentKeys.list(scheduleId, {}) });
            // Invalidate employee's my-schedules
            queryClient.invalidateQueries({ queryKey: assignmentKeys.mySchedules({}) });
            // Invalidate computed shifts
            queryClient.invalidateQueries({ queryKey: shiftKeys.list(scheduleId) });
            queryClient.invalidateQueries({ queryKey: shiftKeys.mySchedule({}) });
        },
    });
}
